/**
 * Self-Hosted Error Tracking System
 * 
 * A complete error tracking solution that doesn't rely on external services.
 * Stores errors locally and provides analytics capabilities.
 * 
 * Features:
 * - Automatic error capture (with filtering for Figma Make internal errors)
 * - Performance tracking
 * - User journey tracking
 * - Analytics and reporting
 * - Privacy-first (all data stored locally)
 */

export interface ErrorLog {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  level: 'error' | 'warning' | 'info';
  context?: Record<string, any>;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId: string;
  tags?: string[];
}

export interface ErrorStats {
  totalErrors: number;
  errorsByLevel: Record<string, number>;
  errorsByType: Record<string, number>;
  recentErrors: ErrorLog[];
  topErrors: Array<{ message: string; count: number }>;
}

export class ErrorTracker {
  private readonly STORAGE_KEY = 'tension_error_logs';
  private readonly MAX_LOGS = 1000; // Keep last 1000 errors
  private readonly SESSION_KEY = 'tension_session_id';
  private sessionId: string;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.setupGlobalHandlers();
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem(this.SESSION_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(this.SESSION_KEY, sessionId);
    }
    return sessionId;
  }

  private shouldIgnoreError(error: any, message: string): boolean {
    // Ignore Figma Make internal errors
    const ignoredPatterns = [
      'IframeMessageAbortError',
      'message port was destroyed',
      'Message aborted',
      'figma.com/webpack-artifacts',
      'ResizeObserver loop',
      'Non-Error promise rejection',
    ];
    
    const errorString = String(error?.message || message || error);
    const stackString = String(error?.stack || '');
    
    return ignoredPatterns.some(pattern => 
      errorString.includes(pattern) || stackString.includes(pattern)
    );
  }

  private setupGlobalHandlers(): void {
    // Handle uncaught errors (filter Figma internal errors)
    window.addEventListener('error', (event) => {
      const error = event.error || new Error(event.message);
      
      // Ignore Figma Make internal errors
      if (this.shouldIgnoreError(error, event.message)) {
        return;
      }
      
      this.logError(error, {
        type: 'uncaught_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Handle unhandled promise rejections (filter Figma internal errors)
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      
      // Ignore Figma Make internal errors
      if (this.shouldIgnoreError(error, String(event.reason))) {
        return;
      }
      
      this.logError(error, {
        type: 'unhandled_rejection',
        promise: 'Promise rejection',
      });
    });

    // Don't override console.error in production to avoid interference
    // Users can manually call logWarning() if needed
  }

  logError(error: Error, context?: Record<string, any>): void {
    if (this.shouldIgnoreError(error, error.message)) {
      return;
    }

    const errorLog: ErrorLog = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      message: error.message,
      stack: error.stack,
      level: 'error',
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      sessionId: this.sessionId,
      tags: this.extractTags(error, context),
    };

    this.saveLog(errorLog);
    this.notifyError(errorLog);
  }

  logWarning(message: string, context?: Record<string, any>): void {
    const errorLog: ErrorLog = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      message,
      level: 'warning',
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      sessionId: this.sessionId,
      tags: context?.tags || [],
    };

    this.saveLog(errorLog);
  }

  logInfo(message: string, context?: Record<string, any>): void {
    const errorLog: ErrorLog = {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      message,
      level: 'info',
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getUserId(),
      sessionId: this.sessionId,
      tags: context?.tags || [],
    };

    this.saveLog(errorLog);
  }

  private saveLog(log: ErrorLog): void {
    try {
      const logs = this.getAllLogs();
      logs.push(log);

      // Keep only the most recent logs
      const trimmedLogs = logs.slice(-this.MAX_LOGS);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmedLogs));

      // Push to backend for admin Error Logs / Error Tracking (fire-and-forget)
      import('./db-storage').then(({ reportError }) => {
        reportError({
          id: log.id,
          timestamp: log.timestamp,
          message: log.message,
          stack: log.stack,
          userAgent: log.userAgent,
          url: log.url,
          userId: log.userId,
          level: log.level,
          sessionId: log.sessionId,
          context: log.context,
          tags: log.tags,
        }).catch(() => {});
      }).catch(() => {});
    } catch (error) {
      // If localStorage is full, remove oldest logs
      const logs = this.getAllLogs();
      const trimmedLogs = logs.slice(-Math.floor(this.MAX_LOGS / 2));
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(trimmedLogs));
    }
  }

  getAllLogs(): ErrorLog[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  getLogsByLevel(level: ErrorLog['level']): ErrorLog[] {
    return this.getAllLogs().filter(log => log.level === level);
  }

  getLogsByTimeRange(startDate: Date, endDate: Date): ErrorLog[] {
    return this.getAllLogs().filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate >= startDate && logDate <= endDate;
    });
  }

  getLogsBySession(sessionId: string): ErrorLog[] {
    return this.getAllLogs().filter(log => log.sessionId === sessionId);
  }

  getStats(): ErrorStats {
    const logs = this.getAllLogs();
    
    // Count by level
    const errorsByLevel: Record<string, number> = {
      error: 0,
      warning: 0,
      info: 0,
    };
    
    // Count by error message
    const errorCounts: Record<string, number> = {};
    
    logs.forEach(log => {
      errorsByLevel[log.level]++;
      errorCounts[log.message] = (errorCounts[log.message] || 0) + 1;
    });

    // Get top errors
    const topErrors = Object.entries(errorCounts)
      .map(([message, count]) => ({ message, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Count by error type
    const errorsByType: Record<string, number> = {};
    logs.forEach(log => {
      const type = log.context?.type || 'unknown';
      errorsByType[type] = (errorsByType[type] || 0) + 1;
    });

    return {
      totalErrors: logs.length,
      errorsByLevel,
      errorsByType,
      recentErrors: logs.slice(-20).reverse(),
      topErrors,
    };
  }

  clearLogs(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  clearOldLogs(daysToKeep: number = 7): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const logs = this.getAllLogs();
    const filteredLogs = logs.filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate > cutoffDate;
    });
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredLogs));
  }

  exportLogs(): string {
    const logs = this.getAllLogs();
    return JSON.stringify(logs, null, 2);
  }

  downloadLogs(): void {
    const logs = this.exportLogs();
    const blob = new Blob([logs], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-error-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private notifyError(log: ErrorLog): void {
    // Only notify for critical errors in production
    if (log.level === 'error' && import.meta.env.PROD) {
      // Could trigger a visual notification or send to a backend endpoint
      console.error('[Error Tracker]', log.message, log.context);
    }
  }

  private generateId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  private getUserId(): string | undefined {
    try {
      const userData = localStorage.getItem('tension_user');
      if (userData) {
        const user = JSON.parse(userData);
        return user.id;
      }
    } catch {
      // Ignore errors
    }
    return undefined;
  }

  private extractTags(error: Error, context?: Record<string, any>): string[] {
    const tags: string[] = [];
    
    // Add error name as tag
    if (error.name) {
      tags.push(error.name);
    }
    
    // Add context tags
    if (context?.tags) {
      tags.push(...context.tags);
    }
    
    // Add component tag from stack trace
    if (error.stack) {
      const componentMatch = error.stack.match(/at (\w+)/);
      if (componentMatch) {
        tags.push(`component:${componentMatch[1]}`);
      }
    }
    
    return tags;
  }

  // Performance tracking
  trackPerformance(metric: string, duration: number, context?: Record<string, any>): void {
    if (duration > 3000) { // Log slow operations
      this.logWarning(`Slow operation: ${metric} took ${duration}ms`, {
        ...context,
        duration,
        metric,
        tags: ['performance'],
      });
    }
  }

  // User action tracking (for debugging user journeys)
  trackUserAction(action: string, context?: Record<string, any>): void {
    this.logInfo(`User action: ${action}`, {
      ...context,
      tags: ['user-action'],
    });
  }
}

// Lazy singleton instance (only initialize when needed)
let errorTrackerInstance: ErrorTracker | null = null;

function getErrorTracker(): ErrorTracker {
  if (!errorTrackerInstance) {
    errorTrackerInstance = new ErrorTracker();
  }
  return errorTrackerInstance;
}

// Export the getter for direct access
export const errorTracker = {
  get instance() {
    return getErrorTracker();
  }
};

// Export convenience methods (these will trigger initialization)
export const logError = (error: Error, context?: Record<string, any>) => 
  getErrorTracker().logError(error, context);

export const logWarning = (message: string, context?: Record<string, any>) => 
  getErrorTracker().logWarning(message, context);

export const logInfo = (message: string, context?: Record<string, any>) => 
  getErrorTracker().logInfo(message, context);

export const trackPerformance = (metric: string, duration: number, context?: Record<string, any>) => 
  getErrorTracker().trackPerformance(metric, duration, context);

export const trackUserAction = (action: string, context?: Record<string, any>) => 
  getErrorTracker().trackUserAction(action, context);
